## Struktura danych
**Struktura danych** to taki pojemnik na dane, który sprawia, że są one przechowywane w uporządkowany sposób. Dzięki temu możemy bardzo łatwo je odnajdować i manipulować. Wybór odpowiedniej struktury zależy od rodzaju danych, jakie chcemy przechowywać oraz od tego, jakie operacje chcemy na nich wykonywać.

W Pythonie istnieje kilka podstawowych struktur danych, z którymi zapoznamy się w tej lekcji. 

## Lista (list)
Wcześniej zdefiniowaliśmy pojęcie zmiennej jako pudełka. W takim razie **listę** możemy sobie wyobrazić jako zestaw takich pudełek, ułożonych obok siebie w ustalonej kolejności. Pozwala nam ona przechowywać wiele obiektów w jednym miejscu.

<img src={new URL('../../img/python_pudelka.png', import.meta.url)} alt="lista jako zestaw pudełek" className={`big_img`}/>

Listę tworzymy za pomocą nawiasów kwadratowych **[]**, w których umieszczamy kolejne elementy, pooddzielane przecinkami. Co istotne, lista może przechowywać elementy o różnych typach. 

```python
>>> lista = [3, 15, -5, 24, 3]
>>> lista 
[3, 15, -5, 24, 3]
>>> lista = ['Ala', 10, 'Ola', True, 10]
>>> lista 
['Ala', 10, 'Ola', True, 10]
>>> type(lista)
<class 'list'>
```
<br/>

Każdy element w liście jest numerowany kolejno od 0. Ten numer przy każdym pudełku nazywamy **indeksem** - wskazuje on pozycję elementu w liście. 

Jeśli chcemy dostać się do wybranego elementu, odwołujemy się do jego indeksu, w następujący sposób  **nazwa_listy[indeks]**.

```python
>>> lista[0]
1
>>> lista[3]
True
```
Możemy również zmodyfikować element listy:
```python
>>> lista[0] = 2023
>>> lista
[2023, 'Ala', 'Ola', True, 10]
```

Możemy również utworzyć pustą listę. W tym celu używamy pustych nawiasów kwadratowych **[]** lub funkcji **list()**.

```python
pusta_lista = []
inna_lista = list()
```
Sama funkcja **list()** służy również do zmiany innej struktury danych, np. słownika czy krotki na listę. Działa więc w analogiczny sposób jak **int()** czy **str()**.

### Więcej o indeksowaniu
Elementy w liście nie są numerowane tylko od przodu (od 0), ale również od tyłu.

<img src={new URL('../../img/python_samochody.png', import.meta.url)} alt="lista jako zestaw pudełek" className={`big_img`}/>

```python
>>> samochody = ['pomarańczowy', 'fioletowy', 'pomarańczowy', 'czerwony', 'zielony']
>>> samochody[-1]
'zielony'
>>> samochody[-5]
'pomarańczowy'
```

Musimy uważać na to, żeby przypadkiem nie odwołać się do nieistniejącego indeksu:

```python!
>>> samochody[5]
IndexError: list index out of range
>>> samochody[-10]
IndexError: list index out of range
```

### List slicing
Poza tym, że za pomocą indeksów możemy dostać się do jednego wybranego elementu, to możemy również "wyciąć" jej kawałek, czyli wyciągnąć z niej dane z podanego zakresu:
```python!
>>> samochody[0:2]
['pomarańczowy', 'fioletowy']
```

W powyższym przykładzie wycięliśmy fragment listy zaczynający się od indeksu 0 (włącznie), a kończący na indeksie 2 (z wyłączeniem tego indeksu). 

Składnia takiego wycinania wygląda następująco:
**nazwa_listy [ indeks_poczatkowy : indeks_końcowy : krok ]**
Jeżeli nie podamy indeksu początkowego, to domyślnie zakres będzie ustawiony na początek listy, a jeżeli końcowego, to na koniec listy. 
Krok mówi nam o tym, co który element z listy wyciągamy.
```python
>>> lista = [1, 2, 3, 4, 5]
>>> lista[:3]
[1, 2, 3]
>>> lista[2:]
[3, 4, 5]
>>> lista[:]
[1, 2, 3, 4, 5]
>>> lista[::2]
[1, 3, 5]
```

Możemy zmodyfikować zakres elementów listy:
```python
>>> lista[0:2] = [100, 90]
>>> lista
[100, 90, 'Ola', True, 10]
```


## Operacje na listach
### Dodawanie elementów
Do już istniejącej listy możemy dodać nowe elementy: 
* Za pomocą funkcji **append(wartość)**, aby dodać go na koniec listy
```python!
>>> owoce = ['jabłko', 'banan', 'kiwi']
>>> owoce.append('ananas')
>>> owoce
['jabłko', 'banan', 'kiwi', 'ananas']
```
* Za pomocą funkcji **insert(indeks, wartość)**, aby dodać go w konkretne miejsce
```python!
>>> owoce.insert(2, 'gruszka')
>>> owoce
['jabłko', 'banan', 'gruszka', 'kiwi', 'ananas']
```

### Łączenie list

Możemy połączyć kilka list w jedną za pomocą operatora **+**: 
```python=
>>> owoce = ['jabłko', 'banan', 'gruszka', 'kiwi', 'ananas']
>>> warzywa = ['marchewka', 'brokuł']
>>> fit_dieta = owoce + warzywa
>>> fit_dieta
['jabłko', 'banan', 'gruszka', 'kiwi', 'ananas', 'marchewka', 'brokuł']
```

### Usuwanie elementów z listy
Używamy **pop()**, aby usunąć ostatni element z listy i zwrócić go jako wynik.
```python
>>> owoce = ['jabłko', 'banan', 'gruszka', 'kiwi', 'ananas']
>>> owoce.pop()
'ananas'
>>> owoce
['jabłko', 'banan', 'gruszka', 'kiwi']
```
Możemy również użyć **pop(indeks)**
```python
>>> owoce.pop(2)
['jabłko', 'banan', 'kiwi']
```
Albo **del**

```python
>>> del owoce[1]
['jabłko', 'kiwi']
```
Możemy też usunąć element za pomocą **remove**. Tym razem nie będziemy podawać indeksu, tylko jego wartość. 

```python=
>>> owoce = ['banan', 'kiwi', 'jabłko', 'kiwi']
>>> owoce.remove('kiwi')
>>> owoce
['banan', 'jabłko', 'kiwi']
```

Zwróć uwagę na to, że **remove** usuwa tylko pierwsze wystąpienie podanej wartości!

### Kopiowanie listy
Na lekcji *Zmienne* poznaliśmy sposób kopiowania zmiennych za pomocą operatora przypisania *=*. W przypadku złożonych struktur danych — takich jak listy — ten sposób się jednak nie sprawdzi.
```python
>>> lista1 = [1, 2, 3]
>>> lista2 = lista1
>>> lista2[1] = 5
>>> lista1 
[1, 5, 3]
```
Zauważ, że zmiana w liście drugiej powoduje także zmianę w pierwszej liście. <br/>
Ale właściwie, dlaczego tak się stało? W przypadku złożonych struktur danych operator przypisania nie kopiuje obiektu, tylko adres pamięci. To oznacza, że *lista1* i *lista2* wskazują na ten sam adres pamięci, a tym samym w pamięci istnieje tylko jedna instancja tej listy. <br/> <br/>

Aby poprawnie utworzyć kopię listy, możemy użyć funkcji **copy()**. 
```python
>>> lista1 = [1, 2, 3]
>>> lista2 = lista1.copy()
>>> lista2[1] = 5
>>> lista1 
[1, 2, 3]
```

albo skorzystać z **list slicing-u**:
```python
>>> lista1 = [1, 2, 3]
>>> lista2 = lista1[:]
>>> lista2[1] = 5
>>> lista1 
[1, 2, 3]
```
Podczas wycinania kawałka listy w pamięci tworzona jest nowa lista. Właśnie dlatego korzystając z operatora **[:]** (który "wycina" całą listę) możemy w prosty sposób zrobić kopię.

### Sortowanie listy
Możemy posortować listę za pomocą funkcji **sort()**

```python
>>> liczby = [10, 2, 14, 5, 1]
>>> liczby.sort()
>>> liczby
[1, 2, 5, 10, 14]
```

Jeżeli chcemy posortować listę malejąco, dodajemy do funkcji sort argument **reverse=True**. 

```python
>>> liczby = [10, 2, 14, 5, 1]
>>> liczby.sort(reverse=True)
>>> liczby
[14, 10, 5, 2, 1]
```

### Odwracanie listy

Możemy odwrócić listę za pomocą **reverse()**

```python 
>>> liczby = [10, 2, 14, 5, 1]
>>> liczby.reverse()
>>> liczby
[1, 5, 14, 2, 10]
```

Albo zamiast powyższej funkcji możemy użyć **slicing'u**:
```python
>>> liczby = [10, 2, 14, 5, 1]
>>> liczby = liczby[::-1]
>>> liczby
[1, 5, 14, 2, 10]
```

### Wyszukiwanie elementu w liście
Aby sprawdzić, czy dany element występuje w liście, używamy operatora **in**.
```python
>>> liczby = [10, 2, 14, 5, 1]
>>> 2 in liczby
True
>>> 3 in liczby
False
```
Nie dostajemy jednak informacji o tym, w którym miejscu listy znajduje się ten element. Jeżeli nam na tym zależy, to zamiast *in* używamy **index()**.
```python
>>> liczby = [10, 2, 14, 5, 14]
>>> liczby.index(14)
2
```
Jeżeli w liście istnieje więcej niż jeden element, który wyszukujemy, to powyższa funkcja zawsze zwróci nam indeks jego pierwszego wystąpienia.

### Zliczanie wystąpień elementu w liście
Aby zliczyć ile razy jakiś element wystąpił w liście, używamy **count()**:
```python
>>> liczby = [10, 1, 2, 10, 10]
>>> liczby.count(10)
3
>>> liczby.count(5)
0
```

## Funkcje wbudowane
Teraz opowiemy o trzech przydatnych funkcjach wbudowanych, których można używać na listach:

Funkcja **len()** zwraca długość podanej struktury
```python
>>> liczby = [1, 2, 3, 4]
>>> len(liczby)
4
```
**min()** zwraca najmniejszy element w strukturze
```python
>>> liczby = [1, 2, 3, 4]
>>> min(liczby)
1
```
Z kolei funkcja **max()** zwraca największy element
```python
>>> liczby = [10, 2, 1, 15, 8]
>>> max(liczby)
15
```

## Listy dwuwymiarowe
Powiedzieliśmy, że listy  mogą zawierać dowolne obiekty. Skoro tak, to mogą zawierać także inne listy! 
```python
>>> lista = [1, [20, 30, 40], 5]
>>> lista[1]
[20, 30, 40]
>>> krotka = ((1, 2), 7, 8, 9)
>>> krotka[0]
(1, 2)
```

W takim razie w prosty sposób możemy utworzyć listę dwuwymiarową:
```python
>>> lista = [[11, 21, 31], [12, 22, 32], [13, 23, 33]]
>>> lista[1]
[12, 22, 32]
>>> lista[1][0]
12
```
